import * as Sentry from "@sentry/react";
import type { ErrorEvent, EventHint } from "@sentry/react";

export type SentryEnv = Pick<
  ImportMetaEnv,
  "PROD" | "MODE" | "VITE_SENTRY_DSN" | "VITE_SENTRY_ENVIRONMENT"
>;

// Matched against the error message and its "Type: message" form; matching
// events are dropped in the browser before they are sent.
export const IGNORE_ERRORS: Array<string | RegExp> = [
  // Dynamic-import failures. lazyWithRetry retries and then reloads the page,
  // so whatever is left is a stale deploy or a flaky network, not a code bug.
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "Unable to preload CSS",
  "Failed to load application chunk after retry",
  // Request cancellations (AbortController / axios CanceledError): the user
  // navigated away or the caller aborted on purpose.
  /^AbortError: /,
  /^CanceledError: /,
  // Benign browser-internal notification that some browsers report as an error.
  "ResizeObserver loop",
];

// Frames from browser extensions are not ours to fix.
export const DENY_URLS: RegExp[] = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
];

// utils/client.js rejects failed requests with a plain { data, message, status }
// object. A dispatch nobody awaits surfaces that as an unhandled rejection, but
// the reducers already show the failure to the user and server errors (5xx)
// are reported by httpClientIntegration, so the event carries no new signal.
export function isApiRejection(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  if (Object.getPrototypeOf(value) !== Object.prototype) return false;
  return "status" in value && "data" in value && "message" in value;
}

export function beforeSend(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null {
  return isApiRejection(hint.originalException) ? null : event;
}

export function getSentryOptions(
  env: SentryEnv = import.meta.env,
): Sentry.BrowserOptions | null {
  // Dev server and Vitest are not production builds. Cypress in CI (test.yml)
  // runs a production build against stubbed APIs — keep it out as well.
  if (!env.PROD || "Cypress" in window) return null;
  // No DSN is baked into the bundle: each deploy path sets VITE_SENTRY_DSN at
  // build time (docs/SENTRY.md), and a build without it reports nowhere.
  if (!env.VITE_SENTRY_DSN) return null;

  // index.html carries the hand-maintained app version on <html>.
  const appVersion = document.documentElement.dataset.appVersion;

  return {
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_SENTRY_ENVIRONMENT || env.MODE,
    release: appVersion ? `js_sklad@${appVersion}` : undefined,
    // Errors only — no performance tracing, no session replay.
    tracesSampleRate: 0,
    integrations: [
      // Failed XHR/fetch responses become events with url, method and status.
      // 4xx are expected application responses (401 session expiry, 422
      // validation) that the UI handles, so only server failures are reported.
      Sentry.httpClientIntegration({ failedRequestStatusCodes: [[500, 599]] }),
    ],
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
    beforeSend,
  };
}

export function initSentry(env?: SentryEnv): void {
  const options = getSentryOptions(env);
  if (options) Sentry.init(options);
}

// Side effect on import: src/index.jsx imports this module first so the SDK
// is initialised before the partner tags are set and before anything renders.
initSentry();
